import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApolloUserService } from '@core/graphql/apolloUser.service';
import { jwtDecode } from 'jwt-decode';
import { SharedService } from '@shared/service/sharedservice';
import { UserClaim } from '@shared/service/constants.services';
import { PackageLimitService } from '@shared/service/package-limit.service';
import { forkJoin } from 'rxjs';
import { take, finalize } from 'rxjs/operators';

@Component({
  selector: 'app-auth',
  templateUrl: './auth.component.html',
  styleUrl: './auth.component.scss'
})
export class AuthComponent implements OnInit {
  isSignIn: boolean = true;
  currentView: 'signIn' | 'forgot' | 'setPassword' = 'signIn';
  isFirstTimeReset: boolean | null = null;
  signInForm!: FormGroup;
  signUpForm!: FormGroup;
  forgotPasswordForm!: FormGroup;
  setPasswordForm!: FormGroup;
  submittedSignInForm = false;
  submittedSignUpForm = false;
  submittedForgotForm = false;
  submittedSetPasswordForm = false;
  progressState: boolean = false;
  showAlert: boolean = false;
  alertProperties = { type: '', message: '' };
  isError: boolean = false;
  loginErrorMessage: string = '';
  passwordVisible: boolean = false;
  resetToken: string | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private userService: ApolloUserService,
    private sharedService: SharedService,
    private packageLimitService: PackageLimitService
  ) { }

  ngOnInit(): void {
    this.initForm();

    this.route.queryParamMap.subscribe(params => {
      const token = params.get('token');
      const mode = params.get('mode');
      const expired = params.get('expired');
      const message = params.get('message');

      // Show trial expiration message if redirected from guard
      if (expired === 'true' && message) {
        this.displayAlert('danger', decodeURIComponent(message));
      }

      if (token) {
        this.loadPasswordResetStatus(token);
      } else if (mode === 'forgot') {
        this.currentView = 'forgot';
        this.isSignIn = false;
        this.submittedForgotForm = false;
        this.forgotPasswordForm.reset();
      } else {
        this.switchToSignIn(false);
      }
    });
  }

  initForm() {
    this.signInForm = this.formBuilder.group({
      email: [null, [Validators.required, Validators.email]],
      password: [null, Validators.required],
    });
    this.signUpForm = this.formBuilder.group({
      firstName: [null, Validators.required],
      lastName: [null, Validators.required],
      email: [null, [Validators.required, Validators.email]],
      password: [null, Validators.required],
    });
    this.forgotPasswordForm = this.formBuilder.group({
      email: [null, [Validators.required, Validators.email]],
    });
    this.setPasswordForm = this.formBuilder.group({
      token: [null, Validators.required],
      password: [null, Validators.required],
      confirmPassword: [null, Validators.required],
    }, { validators: this.passwordsMatchValidator() });
  }

  private loadPasswordResetStatus(token: string): void {
    this.progressState = true;
    this.userService.getPasswordResetStatus(token).subscribe(
      (response) => {
        this.progressState = false;
        if (!response?.isValid) {
          this.displayAlert('danger', response?.error ?? 'Reset link is invalid or has expired.');
          this.switchToSignIn();
          return;
        }

        this.resetToken = token;
        this.isFirstTimeReset = response.isFirstTime === true;
        this.currentView = 'setPassword';
        this.isSignIn = false;
        this.submittedSetPasswordForm = false;
        this.setPasswordForm.reset({
          token: token,
          password: null,
          confirmPassword: null
        });
      },
      () => {
        this.progressState = false;
        this.displayAlert('danger', 'Unable to verify reset link. Please try again.');
        this.switchToSignIn();
      }
    );
  }

  private passwordsMatchValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const password = group.get('password')?.value;
      const confirmPassword = group.get('confirmPassword')?.value;

      if (!password || !confirmPassword) {
        return null;
      }

      return password === confirmPassword ? null : { passwordMismatch: true };
    };
  }

  switchTab(tab: string) {
    if (tab === 'signIn') {
      this.switchToSignIn();
    }
  }

  switchToForgot(): void {
    this.currentView = 'forgot';
    this.isSignIn = false;
    this.submittedForgotForm = false;
    this.forgotPasswordForm.reset();
    this.router.navigate([], { relativeTo: this.route, queryParams: { mode: 'forgot' }, replaceUrl: true });
  }

  switchToSignIn(updateRoute: boolean = true): void {
    this.currentView = 'signIn';
    this.isSignIn = true;
    this.isFirstTimeReset = null;
    this.submittedSignInForm = false;
    this.submittedForgotForm = false;
    this.submittedSetPasswordForm = false;
    this.signInForm.markAsPristine();
    this.signInForm.markAsUntouched();
    this.forgotPasswordForm.reset();
    this.setPasswordForm.reset();
    this.resetToken = null;
    this.isError = false;
    if (updateRoute) {
      this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
    }
  }

  onForgotPassword(): void {
    const trimmedEmail = (this.forgotPasswordForm.value.email || '').trim();
    this.forgotPasswordForm.patchValue({ email: trimmedEmail }, { emitEvent: false });
    this.submittedForgotForm = true;
    if (this.forgotPasswordForm.invalid) {
      return;
    }

    const email = this.forgotPasswordForm.value.email;
    this.progressState = true;
    this.userService.requestPasswordReset({ email }).subscribe(
      (response) => {
        this.progressState = false;
        if (response?.success) {
          this.displayAlert('success', response.success);
          this.switchToSignIn();
        } else {
          this.displayAlert('danger', response?.error ?? 'Unable to process password reset request.');
        }
      },
      () => {
        this.progressState = false;
        this.displayAlert('danger', 'Unable to process password reset request.');
      }
    );
  }

  onResetPassword(): void {
    this.submittedSetPasswordForm = true;
    if (this.resetToken) {
      this.setPasswordForm.patchValue({ token: this.resetToken });
    }
    if (this.setPasswordForm.invalid) {
      return;
    }

    const formValue = this.setPasswordForm.getRawValue();
    const resetInput = {
      token: formValue.token?.trim(),
      password: formValue.password,
      confirmPassword: formValue.confirmPassword
    };

    this.progressState = true;
    this.userService.resetPasswordWithToken(resetInput).subscribe(
      (response) => {
        this.progressState = false;
        if (response?.success) {
          this.displayAlert('success', response.success);
          this.switchToSignIn();
        } else {
          this.displayAlert('danger', response?.error ?? 'Unable to set password.');
        }
      },
      () => {
        this.progressState = false;
        this.displayAlert('danger', 'Unable to set password.');
      }
    );
  }

  onSignIn() {
    const trimmedEmail = (this.signInForm.value.email || '').trim();
    this.signInForm.patchValue({ email: trimmedEmail }, { emitEvent: false });
    this.submittedSignInForm = true;
    if (this.signInForm.invalid) {
      return;
    }

    const formValue = this.signInForm.value;
    const userInput = {
      email: formValue.email,
      password: formValue.password,
    };

    this.progressState = true;
    localStorage.clear();
    this.userService.loginUser(userInput).subscribe(
      (response) => {
        if (response.token && !response.error) {
          const decodedToken = jwtDecode<UserClaim>(response.token);
          const currentYear = new Date().getFullYear();
          decodedToken.SchoolYear = String(currentYear);
          localStorage.setItem("userClaim", JSON.stringify(decodedToken));
          localStorage.setItem("userAccess", JSON.stringify(decodedToken.UserAccess));
          localStorage.setItem("userSecret", response.token);
          localStorage.setItem("userProfile", JSON.stringify(response));
          this.sharedService.setSelectedYear(currentYear);

          const userId = response.id;
          if (userId) {
            this.fetchUserAndNavigate(userId, formValue.email);
          } else {
            this.progressState = false;
            localStorage.clear();
            this.displayAlert("danger", "User ID not found.");
          }
        } else {
          this.progressState = false;
          this.handleLoginError(response.error);
        }
      },
      (error) => {
        this.progressState = false;
        this.handleLoginError("Failed to Login");
      }
    );
  }

  fetchUserAndNavigate(userId: number, email: string): void {
    this.userService.getUserById(userId).pipe(take(1)).subscribe(
      (userResponse) => {
        const currentUser = userResponse;
        if (currentUser) {
          // Prioritize roles dynamically by name (no hardcoded ids): Admin > Staff > Parent
          const roleOrder = ["Admin", "Staff", "Parent"];
          const getPriority = (role: any) => {
            const name = (role?.refRole?.name || '').trim();
            const idx = roleOrder.indexOf(name);
            return idx === -1 ? 99 : idx;
          };

          const sortedUserRoles = [...currentUser.userRoles].sort((a: any, b: any) => getPriority(a) - getPriority(b));

          const primaryRole = sortedUserRoles[0];
          const staffUserRoleId = primaryRole?.id ?? null;
          if (staffUserRoleId != null) {
            localStorage.setItem("staffUserRoleId", JSON.stringify(staffUserRoleId));
          }

          const roles = currentUser.userRoles.map((role: { refRole: { id: number; name: string } }) => ({
            id: role.refRole.id,
            name: role.refRole.name,
          }));

          localStorage.setItem("userRoles", JSON.stringify(roles));
          const hasAdminRole = roles.some((role: { id: number; name: string }) => role.name === "Admin");
          const hasParentRole = roles.some((role: { id: number; name: string }) => role.name === "Parent");
          const hasStaffRole = roles.some((role: { id: number; name: string }) => role.name === "Staff");

          // Get schoolId from token to check trial expiration
          const claimRaw = localStorage.getItem('userClaim');
          let schoolId: number | null = null;
          if (claimRaw) {
            try {
              const parsed = JSON.parse(claimRaw);
              schoolId = Number(parsed.SchoolId) || Number(parsed.schoolId) || null;
            } catch {
              schoolId = null;
            }
          }

          // Check trial expiration before navigation
          if (schoolId) {
            this.packageLimitService.getSchoolConfiguration(schoolId).pipe(take(1)).subscribe(
              (schoolConfig) => {
                console.log('[Login] School Config:', schoolConfig);
                if (schoolConfig) {
                  const limits = this.packageLimitService.getPackageLimits(schoolConfig);
                  console.log('[Login] Package Limits:', limits);
                  const packageName = limits.package.toLowerCase();
                  
                  if (limits.isTrialExpired && packageName === 'trial') {
                    // Trial expired - block login
                    console.log('[Login] Trial expired - blocking login');
                    this.progressState = false;
                    localStorage.clear();
                    this.displayAlert("danger", "Your trial period has expired. Please upgrade your plan to continue.");
                    return;
                  }
                }
                // Trial not expired or not trial package - show success and proceed with navigation
                console.log('[Login] Trial valid - proceeding with login');
                this.progressState = false;
                this.displayAlert("success", "Login Successfully");
                this.navigateToDashboard(hasAdminRole, hasStaffRole, hasParentRole);
              },
              (error) => {
                console.error("Failed to check trial expiration:", error);
                // If we can't check, allow navigation (fail open for now)
                this.progressState = false;
                this.displayAlert("success", "Login Successfully");
                this.navigateToDashboard(hasAdminRole, hasStaffRole, hasParentRole);
              }
            );
          } else {
            // No schoolId - proceed with navigation
            this.progressState = false;
            this.displayAlert("success", "Login Successfully");
            this.navigateToDashboard(hasAdminRole, hasStaffRole, hasParentRole);
          }
        } else {
          this.displayAlert("danger", "User not found in the user list.");
        }
      },
      (error) => {
        console.error("Failed to fetch user roles:", error);
        this.displayAlert("danger", "Failed to fetch user roles.");
      }
    );
  }

  private navigateToDashboard(hasAdminRole: boolean, hasStaffRole: boolean, hasParentRole: boolean): void {
    let activeRole = 'Admin';
    let targetPath = 'dashboard';
    let queryParams: { [key: string]: any } | undefined;

    if (!hasAdminRole) {
      if (hasStaffRole) {
        activeRole = 'Staff';
        targetPath = 'dashboard';
      } else if (hasParentRole) {
        activeRole = 'Parent';
        targetPath = 'mykids';
      }
    }

    this.sharedService.setCurrentRole(activeRole);
    const targetUrl = this.sharedService.buildRoleBasedUrl(targetPath);

    this.sharedService.showCard(activeRole === 'Parent');

    if (queryParams) {
      this.router.navigate([targetUrl], { queryParams });
    } else {
      this.router.navigate([targetUrl]);
    }
  }

  handleLoginError(errorMessage: string): void {
    this.isError = true;
    this.progressState = false;
    this.loginErrorMessage = errorMessage;
    this.displayAlert("danger", errorMessage);
  }

  onSignUp() {
    console.log('Sign Up Data:');
    this.progressState = true;

    setTimeout(() => {
      this.submittedSignUpForm = true;
      this.progressState = false;
    }, 500);
  }

  togglePasswordVisibility(): void {
    this.passwordVisible = !this.passwordVisible;
  }

  get setPasswordTitle(): string {
    if (this.isFirstTimeReset) {
      return 'Setup Password';
    }
    return 'Reset Password';
  }

  get setPasswordDescription(): string {
    if (this.isFirstTimeReset) {
      return 'Create a password to activate your new account.';
    }
    return 'Enter the reset code and choose a new password for your account.';
  }

  get setPasswordButtonLabel(): string {
    return this.isFirstTimeReset ? 'SETUP PASSWORD' : 'RESET PASSWORD';
  }

  displayAlert(type: string, message: string) {
    this.showAlert = true;
    this.isError = type === 'danger';
    this.alertProperties = { type: type, message: message }
    window.setTimeout(() => {
      this.showAlert = false;
    }, 5000)
  }
}
